import { FC } from 'react';
import { Box, Typography, Avatar, AvatarGroup } from '@mui/material';

import styles from './MergeTripCard.module.scss';
import { Trip, TripBuddy } from '../../../../../types/trip.types';
import { DateIcon, MarkerPinIcon } from '../../../../../components/Icons';
import { formatDateRangeWithYear, formatTripDuration } from '../../../../../utils/dateUtils';
import CustomCheckbox from '../../../../../common/CustomCheckbox';
import tripMockDetailsFallback from '../../../../../assets/images/TripMockDetails.jpg';
import { buildAvatarUrl, generateInitials, generateInitialsFromFullName } from '../../../../../utils/avatarUtils';

interface MergeTripCardProps {
  trip: Trip;
  variant: 'selected' | 'selectable';
  isSelected: boolean;
  onToggle: () => void;
}

const MergeTripCard: FC<MergeTripCardProps> = ({ trip, variant, isSelected, onToggle }) => {
  const tripCoverUrl = trip.custom_cover;

  const getDestinationText = () => {
    if (trip.destination_countries && trip.destination_countries.length > 0) {
      return trip.destination_countries.map(country => country.name).join(', ');
    }
    return 'Unknown destination';
  };

  const renderAvatars = () => {
    const raw = (trip.buddies ?? []) as (TripBuddy | number)[];
    const buddies = raw.map(b => (typeof b === 'number' ? ({ id: b } as TripBuddy) : b));

    if (buddies.length === 0) {
      return null;
    }

    return (
      <AvatarGroup
        max={4}
        className={styles.avatarGroup}
      >
        {buddies.map(buddy => {
          const avatarUrl = buildAvatarUrl(buddy.profile_picture);
          const hasSplitOrLogin = Boolean(
            buddy.first_name?.trim() || buddy.last_name?.trim() || buddy.email?.trim() || buddy.username?.trim()
          );
          const hasFullName = Boolean(buddy.full_name?.trim());
          const initials = hasSplitOrLogin
            ? generateInitials(buddy.first_name, buddy.last_name, buddy.email, buddy.username)
            : hasFullName
              ? generateInitialsFromFullName(buddy.full_name, buddy.email)
              : `U${String(buddy.id).slice(-1)}`;

          return (
            <Avatar
              key={buddy.id}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: avatarUrl ? 'transparent' : '#e3f2fd',
                color: avatarUrl ? 'transparent' : '#0098f0',
                fontSize: '10px',
                fontWeight: 600,
              }}
              src={avatarUrl || undefined}
              slotProps={{
                img: {
                  style: {
                    objectFit: 'cover',
                    objectPosition: 'center',
                  },
                },
              }}
            >
              {!avatarUrl && initials}
            </Avatar>
          );
        })}
      </AvatarGroup>
    );
  };

  const renderTripImage = () => (
    <Box
      className={styles.tripImageContainer}
      sx={{
        ...(tripCoverUrl
          ? {
              backgroundImage: `url(${tripCoverUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {
              backgroundImage: `url(${tripMockDetailsFallback})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }),
      }}
    />
  );

  const renderActiveChip = () => {
    const currentDate = new Date();
    const tripStartDate = new Date(trip.start_date);
    const tripEndDate = new Date(trip.end_date);

    const isActive = tripStartDate <= currentDate && tripEndDate >= currentDate;

    if (!isActive) return null;

    return (
      <Box className={styles.activeChip}>
        <Typography variant="h12Semibold">Active</Typography>
      </Box>
    );
  };

  return (
    <Box className={`${styles.mergeTripCard} ${styles[variant]} ${isSelected ? styles.selected : ''}`}>
      <Box className={styles.tripImageWrapper}>{renderTripImage()}</Box>
      <Box className={styles.tripContent}>
        <Box className={styles.tripHeader}>
          <Typography
            variant="h9Semibold"
            className={styles.tripTitle}
          >
            {trip.name}
          </Typography>
          <Typography
            variant="h11"
            className={styles.tripDuration}
          >
            • {formatTripDuration(trip.start_date, trip.end_date)}
          </Typography>
          {renderActiveChip()}
        </Box>

        <Box className={styles.tripDetails}>
          <Box className={styles.tripDate}>
            <DateIcon size={16} />
            <Typography variant="h11">{formatDateRangeWithYear(trip.start_date, trip.end_date)}</Typography>
          </Box>

          <Box className={styles.tripLocation}>
            <Box className={styles.tripMarker}>
              <MarkerPinIcon size={16} />
              <Typography
                variant="h11"
                sx={{ color: '#5E6A81' }}
              >
                {getDestinationText()}
              </Typography>
            </Box>
            {renderAvatars()}
          </Box>
        </Box>
      </Box>

      {variant === 'selectable' && (
        <Box
          className={styles.checkboxContainer}
          onClick={e => e.stopPropagation()}
        >
          <CustomCheckbox
            checked={isSelected}
            onChange={onToggle}
          />
        </Box>
      )}
    </Box>
  );
};

export default MergeTripCard;
